import React from "react";
import AppShell from "@/components/AppShell";
import { StripeTaxQuickSetup } from "@/components/StripeTaxQuickSetup";
import { Link } from "react-router-dom";
import { CheckCircleIcon as CheckCircle, ArrowSquareOutIcon as ArrowSquareOut, ReceiptIcon as Receipt, MapPinIcon as MapPin, QuestionIcon as Question, BuildingsIcon as Buildings, StorefrontIcon as Storefront, CrownIcon as Crown, WrenchIcon as Wrench, InfoIcon as Info, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";

/**
 * Public-auth instructions page — sellers, breeders, and service
 * providers land here from links on:
 *
 *   - /me/store (storefront dashboard "Three things to know" card)
 *   - /escrow/my (breeder escrow dashboard footnote)
 *   - /provider/me (service provider dashboard payouts section)
 *   - /shop (customer-facing tax disclosure footer link)
 *
 * Why a dedicated SELLER-facing page (not just the admin playbook):
 * the admin page at /admin/stripe-tax-setup is internal — it has
 * file paths, code references, migration history. Sellers don't
 * need any of that — they need a clean 5-step "do this in Stripe,
 * then this on the platform" walkthrough they can follow on their
 * phone without help.
 *
 * Keep this page free of internal jargon, file paths, or admin-only
 * concepts. Every CTA links out to the relevant Stripe / CA CDTFA
 * dashboard so the seller can complete each step without ever
 * needing to ask us a question.
 */

const PERSONA_CARDS = [
  {
    id: "breeder",
    Icon: Crown,
    color: "text-gold",
    label: "Breeders selling dogs",
    summary:
      "Live pet sales count as tangible personal property in California — Stripe Tax can calculate, collect, and report it on your behalf when buyers check out through Armstrong K9 Registry escrow.",
    surface: "Marketplace escrow (puppies, grown dogs)",
  },
  {
    id: "storefront",
    Icon: Storefront,
    color: "text-crimson",
    label: "Storefront sellers",
    summary:
      "Food, treats, gear, custom items — anything tangible you sell from your Armstrong K9 Registry Storefront. Stripe Tax adds the right combined state + county + city rate to every checkout.",
    surface: "Storefront product orders",
  },
  {
    id: "provider",
    Icon: Wrench,
    color: "text-ink",
    label: "Service providers",
    summary:
      "Trainers, groomers, walkers, sitters. California exempts most professional services from sales tax — Stripe Tax still tracks your gross revenue per booking so your bookkeeper has clean numbers. If you ALSO sell tangible add-ons (grooming products, training treats), those are taxable and Stripe handles the split automatically.",
    surface: "Service bookings (single + recurring)",
  },
];

const STEPS = [
  {
    n: 1,
    title: "Get a California Seller's Permit (CDTFA)",
    body: (
      <>
        California requires anyone selling taxable goods in the state to hold a{" "}
        <strong>Seller&apos;s Permit</strong> from the California Department of Tax and Fee
        Administration (CDTFA). It&apos;s free, takes about 20 minutes online, and you&apos;ll
        receive your permit number immediately after submission.
        <ul className="mt-2.5 list-disc pl-5 space-y-1 text-[13.5px] text-[#3F3F3F]">
          <li>Have your SSN/EIN, driver&apos;s license, and a business address ready.</li>
          <li>If you sell from your home, your home address is fine for the registration.</li>
          <li>
            Live pets (dogs, puppies) <strong>are taxable</strong> in CA. The CDTFA livestock
            carve-out covers slaughter animals only — companion pets do not qualify.
          </li>
        </ul>
      </>
    ),
    cta: {
      label: "Apply for a Seller's Permit",
      href: "https://onlineservices.cdtfa.ca.gov/_/",
    },
    skipIf: "If you're outside California, register with your home state's tax authority instead — the process below is the same once you have your permit.",
  },
  {
    n: 2,
    title: "Enable Stripe Tax in your Stripe Connect dashboard",
    body: (
      <>
        Sign in to the Stripe Express dashboard linked to your Armstrong K9 Registry account
        (you&apos;ll find a <em>Manage payouts</em> button on your storefront / provider /
        escrow dashboard). Then:
        <ol className="mt-2.5 list-decimal pl-5 space-y-1.5 text-[13.5px] text-[#3F3F3F]">
          <li>Open <strong>Tax</strong> from the left sidebar (or search for &quot;Tax&quot;).</li>
          <li>
            Click <strong>Activate Stripe Tax</strong>. Stripe will ask for your business&apos;
            origin address — use the address on your CDTFA Seller&apos;s Permit so reporting matches.
          </li>
          <li>
            Confirm the product tax categories. Pick <strong>&quot;Pets &amp; pet supplies&quot;</strong> for
            dogs / treats / toys. The default is fine for most sellers.
          </li>
          <li>
            Add your CA <strong>tax registration</strong>: paste your CDTFA permit number under
            &quot;Tax registrations&quot; → &quot;Add registration&quot; → United States → California.
          </li>
        </ol>
        <p className="mt-2.5 text-[13.5px] text-[#3F3F3F]">
          Stripe Tax calculation costs <strong>0.5% per transaction</strong>, capped at $0.50.
          For escrow / storefront / booking flows, this fee comes out of your payout — it&apos;s
          not a separate invoice.
        </p>
      </>
    ),
    cta: {
      label: "Open Stripe Tax docs",
      href: "https://docs.stripe.com/tax/connect",
    },
  },
  {
    n: 3,
    title: "That's it — your next sale auto-collects tax",
    body: (
      <>
        Once Stripe Tax is active on your account, every sale through Armstrong K9 Registry
        automatically gets a tax line:
        <ul className="mt-2.5 list-disc pl-5 space-y-1.5 text-[13.5px] text-[#3F3F3F]">
          <li>
            <strong>Storefront orders:</strong> buyer sees &quot;Tax · $X.XX&quot; on the
            checkout page and on their Stripe receipt.
          </li>
          <li>
            <strong>Escrow dog sales:</strong> the buyer&apos;s authorization includes tax;
            when you release escrow, tax is held on your Stripe account for filing.
          </li>
          <li>
            <strong>Service bookings:</strong> taxable add-ons (e.g. grooming products) are
            itemised separately from the service fee on the receipt.
          </li>
        </ul>
        <p className="mt-2.5 text-[13.5px] text-[#3F3F3F]">
          You&apos;ll see a running total of collected tax per filing period in your Stripe
          dashboard under <em>Tax → Reports</em>. Nothing changes about how Armstrong K9 Registry
          handles your payouts — you still net the same amount minus the standard platform fee.
        </p>
      </>
    ),
  },
  {
    n: 4,
    title: "File and remit to the CDTFA each quarter",
    body: (
      <>
        Stripe Tax <strong>calculates and collects</strong> tax on your behalf, but it does NOT
        automatically remit it to the state — that step is yours. There are two paths:
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div className="border border-[#E5E5E5] rounded-lg p-4 bg-white">
            <p className="font-bold text-ink text-[14px]">Free — file it yourself</p>
            <p className="text-[12.5px] text-[#3F3F3F] mt-1 leading-relaxed">
              Log into your CDTFA online account each quarter, copy the totals from your Stripe
              Tax dashboard&apos;s &quot;Reports&quot; tab, and submit. Takes 5-10 minutes if
              your numbers are clean.
            </p>
          </div>
          <div className="border border-gold/55 rounded-lg p-4 bg-cream/40">
            <p className="font-bold text-ink text-[14px]">Paid — Stripe files for you</p>
            <p className="text-[12.5px] text-[#3F3F3F] mt-1 leading-relaxed">
              Opt into <strong>Stripe Tax Filing</strong> (~$1.20 per filing per state). Stripe
              pulls the numbers, files with the CDTFA, and emails you the confirmation. Set it
              and forget it.
            </p>
          </div>
        </div>
        <p className="mt-3 text-[12.5px] text-[#6B6B6B] leading-relaxed">
          Both options work — the free path is fine for most sellers under $50K/yr; the paid
          add-on saves time once you&apos;re filing in multiple states.
        </p>
      </>
    ),
    cta: {
      label: "Open your CDTFA filing dashboard",
      href: "https://onlineservices.cdtfa.ca.gov/",
    },
  },
  {
    n: 5,
    title: "(Optional) Add other states if you sell across state lines",
    body: (
      <>
        If you ship products to buyers outside California, you might need to register with
        those states once you cross their <strong>economic nexus threshold</strong>
        (usually $100K in sales or 200 transactions per year per state). Stripe Tax tracks
        your per-state totals and shows a banner when you&apos;re approaching a threshold.
        For each new state, add the registration under <em>Tax → Tax registrations</em> in
        Stripe, paste the new permit number, and you&apos;re done.
        <p className="mt-2.5 text-[12.5px] text-[#6B6B6B] leading-relaxed">
          Most kennels never hit the $100K-per-state threshold and only need to file in CA.
          If you sell nationally through your storefront, this becomes relevant — Stripe
          will tell you when.
        </p>
      </>
    ),
  },
];

const FAQ = [
  {
    q: "Is Armstrong K9 Registry collecting tax on my behalf?",
    a: (
      <>
        <strong>No.</strong> Armstrong K9 Registry is the venue — Stripe collects tax
        directly to <em>your</em> Stripe account for every sale, and the total appears in
        <em> your</em> Stripe Tax dashboard. We never touch the tax money. This keeps you
        as the Merchant of Record so your filings line up exactly with what you received.
      </>
    ),
  },
  {
    q: "What happens if I don't enable Stripe Tax yet?",
    a: (
      <>
        Your sales still complete normally — buyers can still buy from you and you still get
        paid. We don&apos;t block checkout on Tax setup. The only difference is your receipts
        won&apos;t itemise tax, and you&apos;ll be responsible for backing the tax out of your
        gross when you file. We strongly recommend enabling Tax before your first sale so the
        math is clean from day one.
      </>
    ),
  },
  {
    q: "Does Stripe Tax handle out-of-state buyers automatically?",
    a: "Yes — Stripe detects the buyer's shipping or billing address and applies the correct rate for that destination, including local county and city add-ons. You don't need to memorise rates or do any math.",
  },
  {
    q: "What if I'm a hobby breeder doing one litter a year?",
    a: (
      <>
        California&apos;s &quot;occasional sale&quot; safe harbor may apply — under 3 sales
        in a 12-month period typically doesn&apos;t require a permit. <strong>Talk to a CA
        tax professional before relying on this</strong>; the threshold has exceptions and
        the CDTFA can audit retroactively. Most active breeders ship more than 3 transactions
        a year (deposits + balances count separately) and need a permit.
      </>
    ),
  },
  {
    q: "I'm not in California — does any of this still apply?",
    a: "Same playbook, different agency. Replace the CDTFA references with your home state's tax authority (e.g. Texas Comptroller, NY Department of Taxation). Stripe Tax supports all 50 states + DC and adapts automatically once you add your home-state registration in step 2.",
  },
  {
    q: "Will buyers see a tax line on the receipt?",
    a: "Yes — once Stripe Tax is active on your account, every checkout shows the subtotal, the tax line, and the total. The Stripe-generated receipt that lands in the buyer's inbox includes the breakdown for their records (and yours).",
  },
  {
    q: "Do I have to charge tax on dog deposits?",
    a: (
      <>
        Yes, if the deposit is non-refundable and applied to the final purchase price. Refundable
        deposits aren&apos;t taxable until they convert to a sale. Stripe Tax handles this
        automatically based on how you collect the deposit — either as a separate sale
        (taxable) or as a refundable hold (not taxed until released).
      </>
    ),
  },
  {
    q: "What about service bookings — does Stripe still collect tax on those?",
    a: "California doesn't tax most professional services (grooming, training, walking, sitting), so Stripe Tax typically calculates $0 for the service fee itself. The booking still flows through Stripe Tax so your dashboard captures gross revenue for income-tax purposes. If you bundle products (e.g. grooming + take-home shampoo), the product portion is taxed and itemised separately.",
  },
];

export default function StripeTaxSetupHelp() {
  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-10" data-testid="stripe-tax-help-page">
        {/* Header */}
        <div className="mb-10">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-2">
            Sellers & breeders
          </p>
          <h1 className="font-spectral text-4xl md:text-5xl lg:text-6xl text-ink leading-tight tracking-tight">
            Setting up Stripe Tax for your sales
          </h1>
          <p className="mt-4 text-[15px] text-[#3F3F3F] max-w-2xl leading-relaxed">
            One-time setup so every sale automatically collects the right sales tax into your
            own Stripe account. Do the three steps below — the fine print further down is there
            if you want it, not because you need it.
          </p>
        </div>

        {/* Action-first checklist — the actual "do this now" experience. */}
        <StripeTaxQuickSetup />

        {/* Who this applies to */}
        <section className="mb-12" data-testid="stripe-tax-personas">
          <div className="flex items-center gap-2 mb-5">
            <Buildings size={20} weight="duotone" className="text-gold" />
            <h2 className="font-spectral text-3xl text-ink">Who needs this</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {PERSONA_CARDS.map(({ id, Icon, color, label, summary, surface }) => (
              <div
                key={id}
                className="border border-[#E5E5E5] rounded-xl bg-white p-5 flex flex-col gap-3"
                data-testid={`persona-card-${id}`}
              >
                <Icon size={26} weight="duotone" className={color} />
                <h3 className="font-spectral text-xl text-ink tracking-tight">{label}</h3>
                <p className="text-[13px] text-[#3F3F3F] leading-relaxed">{summary}</p>
                <p className="text-[11px] text-[#6B6B6B] uppercase tracking-[0.18em] font-semibold mt-auto pt-2 border-t border-[#EEE]">
                  {surface}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* The 5 steps */}
        <section className="mb-12" data-testid="stripe-tax-steps">
          <div className="flex items-center gap-2 mb-5">
            <MapPin size={20} weight="duotone" className="text-crimson" />
            <h2 className="font-spectral text-3xl text-ink">The full details (optional reading)</h2>
          </div>
          <p className="text-[14px] text-[#3F3F3F] leading-relaxed mb-6">
            The whole setup takes about 30 minutes and you only do it once per Stripe account.
            After that, every sale through Armstrong K9 Registry automatically collects tax for
            you.
          </p>
          <ol className="space-y-6">
            {STEPS.map((s) => (
              <li
                key={s.n}
                className="border border-[#E5E5E5] rounded-xl bg-white p-6"
                data-testid={`stripe-tax-step-${s.n}`}
              >
                <div className="flex items-start gap-5">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gold-gradient text-ink font-bold text-[16px] flex-shrink-0 shadow-sm">
                    {s.n}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-spectral text-2xl tracking-tight text-ink mb-3">
                      {s.title}
                    </h3>
                    <div className="text-[14px] text-[#3F3F3F] leading-relaxed">
                      {s.body}
                    </div>
                    {s.skipIf && (
                      <div className="mt-3 flex items-start gap-2 bg-cream/40 border border-[#E2DAC2] rounded-lg p-3">
                        <Info size={14} weight="duotone" className="text-gold-dark flex-shrink-0 mt-0.5" />
                        <p className="text-[12.5px] text-[#3F3F3F] leading-relaxed">{s.skipIf}</p>
                      </div>
                    )}
                    {s.cta && (
                      <a
                        href={s.cta.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 inline-flex items-center gap-2 bg-ink hover:bg-crimson text-white rounded-full px-5 py-2.5 text-[13px] font-semibold transition-colors"
                        data-testid={`stripe-tax-step-${s.n}-cta`}
                      >
                        {s.cta.label}
                        <ArrowSquareOut size={13} weight="bold" />
                      </a>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* What collects what — quick reference */}
        <section className="mb-12" data-testid="stripe-tax-coverage">
          <div className="flex items-center gap-2 mb-5">
            <Receipt size={20} weight="duotone" className="text-ink" />
            <h2 className="font-spectral text-3xl text-ink">What&apos;s covered today</h2>
          </div>
          <div className="border border-[#E5E5E5] rounded-xl bg-white overflow-x-auto">
            <table className="w-full text-[13.5px]" data-testid="coverage-table">
              <thead className="bg-cream/40 border-b border-[#EAEAEA]">
                <tr>
                  <th className="text-left px-5 py-3 font-bold text-ink">Sale type</th>
                  <th className="text-left px-5 py-3 font-bold text-ink">Stripe Tax</th>
                  <th className="text-left px-5 py-3 font-bold text-ink">Filing</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEE]">
                {[
                  ["Storefront product orders", "Automatic", "You file (or Stripe Filing add-on)"],
                  ["Marketplace dog escrow", "Automatic", "You file (or Stripe Filing add-on)"],
                  ["Service bookings (single)", "Automatic — typically $0 for pure services", "You file"],
                  ["Service bookings (recurring)", "Automatic — typically $0 for pure services", "You file"],
                  ["Platform fees (registration, BG check)", "Armstrong files", "Not your responsibility"],
                ].map(([surface, tax, filing]) => (
                  <tr key={surface} className="hover:bg-cream/20">
                    <td className="px-5 py-3 text-ink">{surface}</td>
                    <td className="px-5 py-3 text-[#3F3F3F]">
                      <CheckCircle size={14} weight="fill" className="inline text-emerald-600 mr-1.5 -mt-0.5" />
                      {tax}
                    </td>
                    <td className="px-5 py-3 text-[#3F3F3F]">{filing}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-12" data-testid="stripe-tax-faq">
          <div className="flex items-center gap-2 mb-5">
            <Question size={20} weight="duotone" className="text-gold" />
            <h2 className="font-spectral text-3xl text-ink">Frequently asked</h2>
          </div>
          <div className="space-y-5">
            {FAQ.map((item, i) => (
              <div
                key={i}
                className="border border-[#E5E5E5] rounded-xl bg-white p-5"
                data-testid={`stripe-tax-faq-${i}`}
              >
                <p className="font-bold text-ink mb-2 text-[14.5px]">{item.q}</p>
                <p className="text-[13.5px] text-[#3F3F3F] leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Quick links */}
        <section className="mb-12" data-testid="stripe-tax-links">
          <h2 className="font-spectral text-2xl text-ink mb-4">Useful links</h2>
          <div className="grid sm:grid-cols-2 gap-3 text-[13.5px]">
            <a
              href="https://onlineservices.cdtfa.ca.gov/_/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between border border-[#E5E5E5] rounded-lg px-4 py-3 hover:border-gold hover:bg-cream/30 transition-colors"
              data-testid="link-cdtfa-permit"
            >
              <span>Apply for a CA Seller&apos;s Permit</span>
              <ArrowSquareOut size={14} className="text-gold-dark" />
            </a>
            <a
              href="https://docs.stripe.com/tax/connect"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between border border-[#E5E5E5] rounded-lg px-4 py-3 hover:border-gold hover:bg-cream/30 transition-colors"
              data-testid="link-stripe-tax-docs"
            >
              <span>Stripe Tax on Connect — official docs</span>
              <ArrowSquareOut size={14} className="text-gold-dark" />
            </a>
            <a
              href="https://docs.stripe.com/tax/registrations"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between border border-[#E5E5E5] rounded-lg px-4 py-3 hover:border-gold hover:bg-cream/30 transition-colors"
              data-testid="link-stripe-registrations"
            >
              <span>Adding a tax registration in Stripe</span>
              <ArrowSquareOut size={14} className="text-gold-dark" />
            </a>
            <a
              href="https://www.cdtfa.ca.gov/taxes-and-fees/sutprograms.htm"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between border border-[#E5E5E5] rounded-lg px-4 py-3 hover:border-gold hover:bg-cream/30 transition-colors"
              data-testid="link-cdtfa-sales-tax"
            >
              <span>CA CDTFA sales tax overview</span>
              <ArrowSquareOut size={14} className="text-gold-dark" />
            </a>
          </div>
        </section>

        {/* Disclaimer */}
        <div
          className="border border-[#E5E5E5] rounded-xl bg-cream/30 p-5 text-[12.5px] text-[#3F3F3F] leading-relaxed"
          data-testid="stripe-tax-disclaimer"
        >
          <p>
            <strong>Important.</strong> This page is operational guidance, not tax advice.
            Armstrong K9 Registry is a venue connecting buyers and sellers — we don&apos;t
            file taxes for you and we&apos;re not a substitute for a CPA. If your situation
            is complex (multi-state sales, large litters, breeding-as-a-business deductions,
            agricultural carve-outs), talk to a California-licensed tax professional before
            relying on the steps above. Stripe Tax handles the calculation and collection
            mechanics; you remain responsible for filing accurately and on time.
          </p>
        </div>

        {/* Where to go next */}
        <div
          className="mt-10 pt-8 border-t border-[#EAEAEA] flex flex-col sm:flex-row gap-3"
          data-testid="stripe-tax-next-steps"
        >
          <Link
            to="/me/store"
            className="flex-1 inline-flex items-center justify-between gap-2 bg-ink hover:bg-crimson text-white rounded-full px-5 py-3 text-[13px] font-semibold transition-colors"
            data-testid="next-storefront"
          >
            <span>Open my storefront</span>
            <ArrowRight size={14} weight="bold" />
          </Link>
          <Link
            to="/escrow/my"
            className="flex-1 inline-flex items-center justify-between gap-2 border border-[#E5E5E5] hover:border-gold hover:bg-cream/30 text-ink rounded-full px-5 py-3 text-[13px] font-semibold transition-colors"
            data-testid="next-escrow"
          >
            <span>View my escrow sales</span>
            <ArrowRight size={14} weight="bold" />
          </Link>
          <Link
            to="/provider/me"
            className="flex-1 inline-flex items-center justify-between gap-2 border border-[#E5E5E5] hover:border-gold hover:bg-cream/30 text-ink rounded-full px-5 py-3 text-[13px] font-semibold transition-colors"
            data-testid="next-provider"
          >
            <span>My provider dashboard</span>
            <ArrowRight size={14} weight="bold" />
          </Link>
        </div>

        {/* Cross-link to the 1099-K explainer — sellers landing here
            to set up Stripe Tax are exactly the audience who&apos;ll
            also want to know what 1099-K paperwork hits them. */}
        <div
          className="mt-10 rounded-xl border border-gold/40 bg-[#FBF7EC] p-5 flex items-start gap-3"
          data-testid="stripe-tax-help-1099k-crosslink"
        >
          <Receipt size={20} weight="duotone" className="text-gold-dark flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-spectral text-lg text-ink leading-tight">
              While you&rsquo;re thinking about taxes: 1099-K
            </p>
            <p className="text-[13px] text-[#3F3F3F] mt-1 leading-relaxed">
              Sales tax (this page) and income reporting (1099-K) are two
              different forms. Stripe issues your 1099-K automatically &mdash;
              we explain how it works, what it shows, and when it
              arrives in plain English.
            </p>
            <Link
              to="/help/1099-k-and-taxes"
              className="mt-2 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-crimson hover:underline"
              data-testid="stripe-tax-help-1099k-link"
            >
              Read the 1099-K explainer
              <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
